import React, {
  Dispatch,
  forwardRef,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames/dedupe";
import { Button } from "../../../ActionsSelectors/Button";
import { VideoPauseIcon, VideoPlayLargeIcon } from "../../../../icons";
import useEventListener from "../../../../hooks/useEventListener";

export type TVideoSource = {
  src: string;
  type?: string;
};

export interface IVideoControls {
  playing: boolean;
  setPlaying: Dispatch<SetStateAction<boolean>>;
}

export interface IVideoWidgetProps
  extends Partial<
    Pick<
      JSX.IntrinsicElements["video"],
      "autoPlay" | "muted" | "loop" | "poster"
    >
  > {
  sources: TVideoSource[];
  notSupportedText?: ReactNode;
  showPlayButton?: boolean;
  toggleOnClick?: boolean;
  classes?: {
    root?: string;
    video?: string;
  };
  onPlay?(controls: IVideoControls): void;
  onPause?(controls: IVideoControls): void;
  onClick?(controls: IVideoControls): void;
  onEnd?(controls: IVideoControls): void;
}

const VideoWidget = forwardRef<HTMLVideoElement, IVideoWidgetProps>(
  (
    {
      sources,
      autoPlay = true,
      muted = true,
      loop = true,
      notSupportedText = "Sorry, your browser doesn't support embedded videos.",
      showPlayButton = false,
      toggleOnClick = false,
      poster,
      classes = {
        root: "position-relative",
        video: "",
      },
      onPlay,
      onPause,
      onClick,
      onEnd,
    },
    ref,
  ) => {
    const [playing, setPlaying] = useState(autoPlay);
    const localRef = useRef<HTMLVideoElement>(null);
    const videoRef =
      (ref as MutableRefObject<HTMLVideoElement | null>) || localRef;

    const handleEnd = useCallback(() => {
      if (!loop) {
        setPlaying(false);
      }
      onEnd && onEnd({ playing, setPlaying });
    }, [loop, onEnd, playing]);

    const togglePlay = useCallback(
      (event?) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        setPlaying(!playing);
      },
      [playing],
    );

    const handlePlay = useCallback(() => {
      setPlaying(true);
      onPlay && onPlay({ playing, setPlaying });
    }, [onPlay, playing]);
    const handlePause = useCallback(() => {
      setPlaying(false);
      onPause && onPause({ playing, setPlaying });
    }, [onPause, playing]);

    useEventListener("ended", handleEnd, undefined, videoRef);
    useEventListener("play", handlePlay, undefined, videoRef);
    useEventListener("pause", handlePause, undefined, videoRef);

    const handleClick = useCallback(() => {
      if (toggleOnClick) {
        setPlaying(!playing);
      }
      onClick && onClick({ playing, setPlaying });
    }, [onClick, playing, toggleOnClick]);

    useEffect(() => {
      /** sync react state and element's state */
      if (playing) {
        if (videoRef.current?.paused) {
          videoRef.current?.play();
        }
      } else if (!videoRef.current?.paused) {
        videoRef.current?.pause();
      }
    }, [playing, videoRef]);

    return (
      <div className={classes?.root}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          className={classes?.video}
          src={sources[0]?.src}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          poster={poster}
          ref={videoRef}
          onClick={handleClick}
        >
          {sources.map(({ src, type }) => (
            <source key={src} src={src} type={type} />
          ))}

          {notSupportedText}
        </video>
        <Button
          data-testid="VideoWidget__play-button"
          variant="icon"
          classes={{
            root: classNames(
              "position-absolute top-50 start-50 translate-middle btn-animated-fade",
              {
                "uds-fade-out": !showPlayButton,
              },
            ),
          }}
          onClickCapture={togglePlay}
        >
          {playing ? <VideoPlayLargeIcon /> : <VideoPauseIcon />}
        </Button>
      </div>
    );
  },
);

export default VideoWidget;
